import { Formik, Field, Form, ErrorMessage } from "formik";
import ReactQuill from "react-quill"; // Import React Quill
import "react-quill/dist/quill.snow.css"; // Import the Quill CSS
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from "../../loader/Loader";
import {
  useCreateProductMutation,
  useGetProductByIdQuery,
  useUpdateProductMutation,
} from "../../../features/api/productApiSlice";

const AddEditProduct = () => {
  const navigate = useNavigate();
  const params = useParams();
  const productId = params?.id;
  const [editingMode, setEditingMode] = useState(false);
  const [description, setDescription] = useState("");
  const [createProduct, { isLoading: isAddProductLoading }] =
    useCreateProductMutation();
  const [updateProduct, { isLoading: isUpdateProductLoading }] =
    useUpdateProductMutation();
  const { data: product, isLoading: productLoading } = useGetProductByIdQuery(
    productId,
    {
      skip: !productId, // ✅ don’t fetch if no id
    }
  );
  const [uploadState, setUploadState] = useState({});
  useEffect(() => {
    if (productId) {
      setEditingMode(true);
    }
  }, [productId]);

  const handleSubmit = async (values, actions) => {
    try {
      if (editingMode) {
        // ✅ UPDATE FLOW
        const response = await updateProduct({
          id: productId,
          ...values,
          mainImage: values.mainImage,
          images: values.images,
        }).unwrap();

        toast.success(response?.message || "Product updated!");
      } else {
        // ✅ CREATE FLOW
        const response = await createProduct({
          ...values,
          mainImage: values.mainImage,
          images: values.images,
        }).unwrap();

        toast.success(response?.message || "Product created!");
        actions.resetForm();
      }

      navigate("/admin/products");
    } catch (error) {
      toast.error(
        error?.data?.message || "An error occurred, please try again."
      );
    }
  };

  if (isAddProductLoading || productLoading || isUpdateProductLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">
            {editingMode ? "Edit Product" : "Add Product"}
          </h1>
        </div>
      </div>
      <section className="dashboard-overview">
        <Formik
          enableReinitialize
          initialValues={{
            id: productId,
            title: product?.title || "",
            description: product?.description || "",
            price: product?.price || "",
            slug: product?.slug || "",
            category: product?.category || "",
            productUrl: product?.productUrl || "",
            inStock: product?.inStock ?? true,
            mainImage: product?.mainImage || "",
            images: product?.images || [],
          }}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <Form className="dashboard-form">
              <div className="input-flex">
                <div className="form-controls">
                  <label htmlFor="title">Product Title</label>
                  <Field type="text" name="title" />
                  <ErrorMessage
                    name="title"
                    component="div"
                    className="error"
                  />
                </div>

                <div className="form-controls">
                  <label htmlFor="price">Price</label>
                  <Field type="number" name="price" />
                  <ErrorMessage
                    name="price"
                    component="div"
                    className="error"
                  />
                </div>

                <div className="form-controls">
                  <label htmlFor="productUrl">Product Website Url</label>
                  <Field type="text" name="productUrl" />
                  <ErrorMessage
                    name="productUrl"
                    component="div"
                    className="error"
                  />
                </div>

                <div className="form-controls">
                  <label htmlFor="slug">Slug</label>
                  <Field type="text" name="slug" />
                  <ErrorMessage name="slug" component="div" className="error" />
                </div>
              </div>

              <div>
                <label htmlFor="description">Description:</label>
                <ReactQuill
                  className="custom-quill-editor"
                  value={values.description || ""}
                  onChange={(value) => {
                    setDescription(value);
                    setFieldValue("description", value);
                  }}
                  name="description"
                />
                <ErrorMessage
                  name="description"
                  component="div"
                  className="error"
                />
              </div>

              <button
                className="form-button"
                type="submit"
                disabled={isSubmitting}
              >
                {editingMode ? "Edit Product" : "Add Product"}
              </button>
            </Form>
          )}
        </Formik>
      </section>
    </div>
  );
};

export default AddEditProduct;
